import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, ExternalLink, Inbox, LogOut, MessageSquare, RefreshCw, ShieldAlert } from 'lucide-react'
import { Button, ButtonLink } from '../ui/Button.jsx'
import { getFormattedWhatsappNumber } from '../../data/demoStore.js'
import { useAuth } from '../../features/auth/AuthContext.jsx'
import { platformSettingsApi } from '../../services/plotfyApi.js'

export function ErrorState({ title = 'Data belum dapat dimuat', message = 'Periksa koneksi lalu coba lagi.', onRetry, compact = false }) {
  const auth = useAuth?.()
  const [waNumber, setWaNumber] = useState(() => getFormattedWhatsappNumber())
  const isSuspended =
    String(message || '').toLowerCase().includes('ditangguhkan') ||
    String(message || '').toLowerCase().includes('suspend') ||
    String(title || '').toLowerCase().includes('suspend')

  useEffect(() => {
    if (isSuspended) {
      platformSettingsApi
        .getPublicSettings()
        .then((res) => {
          if (res?.formattedWhatsappNumber) {
            setWaNumber(res.formattedWhatsappNumber)
          } else if (res?.adminWhatsappNumber) {
            setWaNumber(getFormattedWhatsappNumber(res.adminWhatsappNumber))
          }
        })
        .catch(() => {})
    }
  }, [isSuspended])

  if (isSuspended) {
    const waMessage = encodeURIComponent(
      `Halo Admin Plotfy,\n\nAkun saya (${auth?.user?.email || auth?.user?.username || 'Pengguna'}) saat ini berstatus SUSPENDED (Ditangguhkan).\nMohon bantuan untuk konfirmasi dan proses pemulihan akun saya.\n\nTerima kasih.`
    )
    const waUrl = `https://wa.me/${waNumber}?text=${waMessage}`

    return (
      <div
        className={`surface-card flex flex-col items-center justify-center text-center border border-red-500/30 bg-ink-900/90 shadow-2xl rounded-2xl ${
          compact ? 'p-5 sm:p-6' : 'p-6 sm:p-10'
        }`}
        role="alert"
      >
        <div className="grid size-12 sm:size-14 place-items-center rounded-2xl border border-red-500/30 bg-red-500/10 text-red-400">
          <ShieldAlert className="size-6 sm:size-7 animate-pulse" aria-hidden="true" />
        </div>

        <p className="mt-4 text-[10px] sm:text-xs font-bold uppercase tracking-[.14em] text-red-400">
          Status: Akun Ditangguhkan
        </p>

        <h2 className="mt-1.5 text-lg sm:text-2xl font-bold tracking-tight text-white">
          Akses Akun Dibatasi
        </h2>

        <p className="mx-auto mt-2 max-w-lg text-xs sm:text-sm leading-relaxed text-copy-muted">
          {message || 'Akun Anda sedang ditangguhkan oleh Administrator Plotfy. Seluruh transaksi dan menu operasional dinonaktifkan sementara.'}
        </p>

        <div className="mt-5 sm:mt-6 flex flex-wrap items-center justify-center gap-2.5 sm:gap-3 w-full max-w-md">
          <a
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            className="flex-1 min-w-[170px] inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-xs sm:text-sm font-bold text-white shadow-lg hover:bg-emerald-500 transition-colors cursor-pointer"
          >
            <MessageSquare className="size-4" />
            <span>Hubungi Admin via WA</span>
          </a>

          <ButtonLink
            to="/account-suspended"
            variant="secondary"
            className="inline-flex items-center justify-center gap-1.5 text-xs py-2.5 px-3.5"
          >
            <ExternalLink className="size-3.5" />
            <span>Info Akun</span>
          </ButtonLink>
        </div>
      </div>
    )
  }

  return (
    <div className={`surface-card flex flex-col items-center justify-center text-center ${compact ? 'min-h-44 p-5' : 'min-h-72 p-8'}`} role="alert">
      <span className="grid size-11 place-items-center rounded-xl bg-red-500/10 text-red-400">
        <AlertCircle className="size-5" aria-hidden="true" />
      </span>
      <h2 className="mt-4 text-base font-semibold text-copy">{title}</h2>
      <p className="mt-1 max-w-md text-sm leading-6 text-copy-muted">{message}</p>
      {onRetry && (
        <Button variant="secondary" className="mt-5" onClick={onRetry}>
          <RefreshCw className="size-4" aria-hidden="true" />
          Coba lagi
        </Button>
      )}
    </div>
  )
}

export function EmptyState({ title = 'Belum ada data', message, actionLabel, actionTo, icon: Icon = Inbox, compact = false }) {
  return (
    <div className={`surface-card flex flex-col items-center justify-center text-center ${compact ? 'min-h-44 p-5' : 'min-h-72 p-8'}`}>
      <span className="grid size-11 place-items-center rounded-xl bg-brand-500/10 text-brand-300">
        <Icon className="size-5" aria-hidden="true" />
      </span>
      <h2 className="mt-4 text-base font-semibold text-copy">{title}</h2>
      {message && <p className="mt-1 max-w-md text-sm leading-6 text-copy-muted">{message}</p>}
      {actionLabel && actionTo && <ButtonLink to={actionTo} className="mt-5">{actionLabel}</ButtonLink>}
    </div>
  )
}

export function DemoNotice({ children = 'Menampilkan data pratinjau karena server belum terhubung.' }) {
  return (
    <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-blue-200 bg-blue-50/90 px-3.5 py-3 text-xs leading-5 text-blue-900 font-medium" role="status">
      <AlertCircle className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden="true" />
      <span><strong className="font-bold text-blue-950">Mode pratinjau.</strong> {children}</span>
    </div>
  )
}

export function PageLoader({ label = 'Memuat halaman' }) {
  return (
    <div className="grid min-h-[55vh] place-items-center" role="status">
      <div className="flex items-center gap-3 text-sm text-copy-muted">
        <span className="size-5 animate-spin rounded-full border-2 border-line-strong border-t-brand-500" aria-hidden="true" />
        {label}
      </div>
    </div>
  )
}

export function CardGridSkeleton({ count = 6 }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" aria-label="Memuat campaign" role="status">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="surface-card overflow-hidden">
          <div className="skeleton aspect-[16/10]" />
          <div className="space-y-3 p-4">
            <div className="skeleton h-6 w-20 rounded-full" />
            <div className="skeleton h-5 w-4/5 rounded" />
            <div className="skeleton h-4 w-1/2 rounded" />
            <div className="skeleton h-6 w-3/5 rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}
